import { useMemo } from 'react';
import { inSpeedRun, madeDuringRun } from '@/lib/speedRun';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useFetchAllPages } from './useFetchAllPages';
import { subscriptionApi, traderApi } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { shownTraderName } from '@/lib/traderSource';

interface Subscription {
  id: number;
  user_id: string;
  credential_id: number;
  trader_uid: string;
  model_name: string;
  model_config: string;
  status: string;
  starting_balance: number;
  total_pnl: number;
  consecutive_losses: number;
  kill_switch_triggered: boolean;
  created_at: string;
  updated_at: string;
  /** When it actually stopped. Absent on sessions that ended before the column
   *  existed, and on every running one. */
  stopped_at?: string | null;
  /** A model.Stop* code: manual, killswitch_pnl, killswitch_drawdown,
   *  killswitch_streak, credential_deleted, deleted. */
  stop_reason?: string | null;
  /** The sentence a user reads, with the numbers that tripped the rule. */
  stop_detail?: string | null;
}

/****************
 * Exported so the session-create warnings ask the SAME question every other
 * screen asks. M-76, 2026-09-23.
 *
 * The two "you already have a session" warnings each filtered on their own
 * terms -- trader + status, credential + status -- and neither looked at the
 * destination at all, while one of them carried a comment claiming Paper and
 * Telegram were excluded. So starting a Telegram session on a trader already
 * copied live raised a warning about sharing a broker key that Telegram never
 * touches.
 *
 * There is exactly one correct answer to "is this session live" in this
 * codebase and it is below. A third spelling of it in the page would be the
 * thing that drifts next.
 ****************/
export function sessionKindOf(sub: Subscription, credIsTestnet?: boolean): 'LIVE' | 'PAPER' | 'TELEGRAM' {
  return mapSubscriptionToSession(sub, credIsTestnet).session_type as 'LIVE' | 'PAPER' | 'TELEGRAM';
}

function mapSubscriptionToSession(sub: Subscription, credIsTestnet?: boolean) {
  const isStopped = sub.status === 'stopped';
  /*
   * stopped_at, not updated_at.
   *
   * updated_at is bumped by every later write to the row -- including the CLOSE
   * fills the stop itself triggers -- so it systematically overshoots the real
   * ending. Three of one account's sessions carry an updated_at inside the same
   * 560ms window, which no human produced.
   *
   * It stays the fallback for sessions stopped before stopped_at existed: a
   * roughly-right end time beats a duration that ticks forever. Sessions with a
   * real stopped_at get the real one.
   */
  const endTime = isStopped ? (sub.stopped_at || sub.updated_at) : null;
  // Duration must freeze when the session ends. Using Date.now() unconditionally
  // (the old behavior) made ended sessions tick forever in the UI.
  const referenceMs = endTime ? new Date(endTime).getTime() : Date.now();
  // Prefer the backend's authoritative is_paper flag (covers SIM_ traders,
  // credential_id=0, and deleted credentials). Fall back to the local heuristic
  // for older payloads. A SIM/paper session must never look LIVE — its orders
  // never hit a broker. 2026-06-10.
  /*
   * Telegram is a session type here, as it already is on mobile.
   *
   * The web knew only PAPER and LIVE, and the backend's `is_paper` is true for
   * any session with no credential — which is every signal session. So a
   * Telegram session was PAPER to every consumer of this mapper: it went into
   * the Paper tab, its declared wallet was added to paper equity, and its
   * orders — orders that reached no book anywhere — were drawn in the Long/Short,
   * Asset and Leverage charts. Guillaume found all three on 2026-09-05.
   *
   * `delivery_mode` is asked first because it is the field that NAMES the type.
   * `is_paper` stays underneath it for sessions created before that column
   * existed.
   */
  /*
   * DELETING A KEY MUST NOT REWRITE WHAT ITS SESSIONS WERE. 2026-09-12.
   *
   * Guillaume: "deleting the API used by a Live session flips the ticker from
   * Live to Paper". It does, and the row is what flips, not the badge --
   * DeleteCredential sets credential_id to 0 on every session that used the key
   * before it hard-deletes it, because the foreign key will not let the row go
   * otherwise. The `credential_id === 0` clause below then read that detached
   * live session as paper.
   *
   * So keylessness is demoted to what it always was: a fallback for rows
   * written before delivery_mode existed, which is the only case where the
   * session has no record of its own type. A session that DECLARES broker keeps
   * that declaration after the key is gone.
   *
   * The default is '' and no longer 'broker'. Defaulting a missing column to
   * 'broker' here would make every pre-column row look declared, and the
   * fallback that is the entire point of this comment would never fire -- while
   * the backend, which defaults it to nothing, went on calling those same rows
   * paper. Two answers for one session is the bug this mapper already exists to
   * prevent.
   */
  const declaredMode = String((sub as any).delivery_mode || '').toLowerCase();
  const isTelegram = declaredMode === 'telegram';
  const isPaper = !isTelegram && (
    declaredMode === 'paper'
    || String(sub.trader_uid || '').startsWith('SIM_')
    || credIsTestnet === true
    // Authoritative: the backend asks store.SessionKindOf, which knows all of
    // the above plus the key's testnet flag.
    || (sub as any).is_paper === true
    // Legacy rows only -- see the comment above.
    || (declaredMode !== 'broker' && sub.credential_id === 0)
  );
  const sessionType = isTelegram ? 'TELEGRAM' : isPaper ? 'PAPER' : 'LIVE';
  return {
    id: String(sub.id),
    user_id: sub.user_id,
    credential_id: sub.credential_id,
    credential_is_testnet: credIsTestnet ?? null,
    model_name: sub.model_name,
    model_config: sub.model_config,
    session_type: sessionType,
    // Emitted as well as session_type. The section below read `is_paper` and
    // got undefined -- falsy -- so every paper session classified as LIVE and
    // the Paper tab was permanently empty. Both names now carry the same
    // answer, computed once. 2026-09-03.
    is_paper: isPaper,
    // Real money on a key we still hold (store.PlacesRealOrders). Listed here
    // because this mapper drops what it does not name, and the Dashboard's live
    // Net PnL needs it: a session whose key was deleted is not paper, but its
    // result is on no wallet we hold. 2026-09-28.
    places_real_orders: (sub as any).places_real_orders,
    // "broker" or "telegram". This mapper builds an explicit object, so a field
    // the backend sends but nobody lists here is silently dropped — which is
    // how a signal session would have shown up as an ordinary paper one with
    // $0.00 PnL and 0 executed. 2026-09-03.
    delivery_mode: (sub as any).delivery_mode || 'broker',
    status: sub.status,
    trader_uid: sub.trader_uid,
    /* A session whose trader's name did not arrive showed the id as its title
       -- "mexc:01146489" -- which names the exchange the trader is on.
       Guillaume, 2026-10-01: "Source should only be visible by admins." */
    trader_name: shownTraderName((sub as any).trader_name, sub.trader_uid),
    /****************
     * REALISED, FROM THE FIGURE THAT HAS BEEN CHECKED AGAINST THE EXCHANGE.
     *
     * Guillaume, 2026-09-06: "fix the $109.77 discrepancy between user_trades
     * and total_pnl." Measured across the platform it is $3,179 over 21
     * sessions, and it was this line.
     *
     * The response carries BOTH numbers. `realized_pnl` is summed from
     * user_trades -- closed lifecycles, both prices present -- and
     * trade_truth_from_broker rewrites those values from the venue's own
     * figures, keeping the originals in a backup table. `total_pnl` is a
     * running field updated on each fill that nothing ever reconciles.
     *
     * Two independent sources agree against it: session 31 reads 236.74 stored
     * against 1835.69 from lifecycles and 1835.69 from the per-fill ledger. It
     * is not that the stored figure lost a vote, it is that only one of these
     * has ever been checked against the exchange.
     *
     * ── ...and the fallback is not a nicety ────────────────────────────────
     *
     * Sessions from March and April have no lifecycles at all -- zero counted,
     * zero fills, a non-zero stored figure. For those, total_pnl is not the
     * worse record, it is the ONLY record, and preferring lifecycles blindly
     * would report them as having made nothing. A fresh lie told to fix an old
     * one. So: lifecycles where the session has any, stored where it has none.
     *
     * Same rule as RealisedBySession on the server, deliberately. The account
     * total and these rows disagreeing is exactly what was reported.
     ****************/
    user_total_pnl_usd:
      ((sub as any).trade_count || 0) > 0
        ? ((sub as any).realized_pnl ?? sub.total_pnl ?? 0)
        : (sub.total_pnl || 0),
    // Open-position PnL. The backend used to hardcode this to 0, so every
    // surface that read it silently showed realised-only. 2026-08-31.
    user_unrealized_pnl_usd: (sub as any).unrealized_pnl || 0,
    trader_total_pnl_usd: (sub as any).trader_total_pnl || 0,
    trader_trade_count: (sub as any).trader_trade_count || 0,
    trader_win_rate: (sub as any).trader_win_rate || 0,
    win_count: (sub as any).win_count || 0,
    loss_count: (sub as any).loss_count || 0,
    open_positions: (sub as any).open_positions || 0,
    // Positions the BROKER holds on this session's key that we cannot pin to a
    // session. open_positions above is OUR count, and on a key shared by
    // several sessions it can be 0 while the account holds several -- which is
    // a false statement, not a limitation. 2026-09-05.
    unattributed_positions: (sub as any).unattributed_positions || 0,
    unattributed_symbols: (sub as any).unattributed_symbols || [],
    sessions_on_credential: (sub as any).sessions_on_credential || 0,
    starting_balance: sub.starting_balance,
    // A member of a group session (backend group_sessions.go): shown inside its group's card.
    group_session_id: (sub as any).group_session_id || undefined,
    group_name: (sub as any).group_name || undefined,
    // Stored snapshot of the trader's wallet at session open (null on older
    // sessions). SessionDetails draws the trader equity line ONLY from this —
    // never from a live wallet_now minus session_pnl recompute, which slides
    // between renders and can start the curve below zero. 2026-08-29 (B07).
    trader_wallet_at_start: (sub as any).trader_wallet_at_start ?? null,
    start_time: sub.created_at,
    end_time: endTime,
    // last activity timestamp used for sorting and the "ended N ago" label.
    last_activity: sub.updated_at || sub.created_at,
    duration_seconds: sub.created_at
      ? Math.max(0, Math.floor((referenceMs - new Date(sub.created_at).getTime()) / 1000))
      : 0,
    user_copied_orders: (sub as any).executed_count || 0,
    user_ignored_orders: (sub as any).missed_count || 0,
    user_adjusted_orders: 0,
    missed_count: (sub as any).missed_count || 0,
    // The two halves of missed_count. A skipped order is your rules working;
    // a failed one means something is wrong and needs you. 2026-09-02.
    skipped_count: (sub as any).skipped_count || 0,
    failed_count: (sub as any).failed_count || 0,
    executed_count: (sub as any).executed_count || 0,
    // Accepted by the exchange and never confirmed traded. Used to be inside
    // executed_count, so a session reported orders as placed that nothing had
    // established were. 2026-09-05.
    unconfirmed_count: (sub as any).unconfirmed_count || 0,
    // Completed trade lifecycles (user_trades), NOT orders — the "Trades" column
    // was falling back to user_copied_orders (=executed_count). 2026-07-06.
    trade_count: (sub as any).trade_count || 0,
    // CLOSED lifecycles is what trade_count means; the open ones are a separate
    // field the backend has always sent and the mapper always dropped. Guillaume
    // spotted it from the UI: "I think it only refer to the closed ones".
    open_trade_count: (sub as any).open_trade_count || 0,
    // Which broker this session trades on, lowercase. Empty for paper and
    // Telegram sessions, which trade on none.
    broker: (sub as any).broker || '',
    // Master trader can no longer be monitored (portfolio closed / unfunded).
    // The user needs to stop the session. 2026-08-26.
    trader_inaccessible: (sub as any).trader_inaccessible === true,
    trader_warning: (sub as any).trader_warning || '',
    /*
     * THE USER DELETED THIS SESSION, AND ITS CLOSED TRADES STILL HAPPENED.
     *
     * The row comes back only when it holds closed trades. Every TOTAL must
     * count it -- Gianni deleted two sessions holding 25.376240 of realised on
     * 2026-09-07 and his lifetime figure has been short by exactly that since,
     * because gorm.DeletedAt removed them from the money as well as the list.
     *
     * No LIST may show it. Anything rendering sessions filters on this; the
     * sums deliberately do not.
     */
    deleted: (sub as any).deleted === true,
    kill_switch_triggered: sub.kill_switch_triggered,
  };
}

export function useSessions(demoMode = false) {
  const { user } = useAuth();

  return useQuery({
    // A speed run (lib/speedRun.ts) sees only what was made during the run.
    queryKey: ['subscriptions', user?.id, inSpeedRun()],
    queryFn: async () => {
      // The credentials lookup only decides a paper/live BADGE, and it used to
      // be able to take the whole query down with it: the `.catch(() => [])`
      // sat on getAllCredentials, not on the dynamic `import()` in front of it,
      // so a chunk that failed to load — which is what happens to an open tab
      // after a deploy replaces the hashed chunk files — threw out of the
      // queryFn before Promise.all ever saw it. The sessions then never
      // arrived, the dashboard rendered $0.00 and 0 trades, and nothing said
      // why. Guard the import itself. 2026-09-03.
      const credsPromise = import('@/services/brokerCredentials')
        .then((m) => m.brokerCredentialsService.getAllCredentials({ includeRemoved: true }))
        .catch(() => [] as any[]);
      const [subs, creds] = await Promise.all([subscriptionApi.getAll(), credsPromise]);
      const testnetById = new Map<string, boolean>();
      for (const c of creds || []) {
        testnetById.set(String((c as any).id), Boolean((c as any).is_testnet));
      }
      return (subs || []).filter((sub: any) => madeDuringRun(sub.created_at)).map((sub: any) =>
        mapSubscriptionToSession(sub, testnetById.get(String(sub.credential_id)))
      );
    },
    enabled: !!user?.id,
    // Coming back to the dashboard should show what we already have while any
    // refetch happens behind it, rather than emptying the tiles first.
    staleTime: 15_000,
    // Left to inference. Annotating this `(previous: unknown)` widened the whole
    // query's data to `unknown`, and every consumer of useSessions inherited it:
    // `sessions.find`, `.length` and even iterating the list became type errors
    // in eight places across four files. None of them were real bugs -- the data
    // is a Session[] at runtime -- but they were the noise that made the
    // typecheck look not worth turning on.
    placeholderData: (previous) => previous,
    // The default three retries back off 1s, 2s, 4s — all of which fit inside a
    // single backend restart window, so one unlucky moment left the query in a
    // permanent error state until the page was remounted. That is exactly the
    // "it only appears after I visit My Sessions and come back" report.
    retry: 5,
    retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 15_000),
  });
}

export function useSession(sessionId: string, demoMode = false) {
  const { data: sessions } = useSessions(demoMode);

  return useQuery({
    queryKey: ['subscription', sessionId],
    queryFn: async () => {
      const session = sessions?.find((s: any) => s.id === sessionId);
      if (!session) throw new Error('Session not found');

      // Enrich with trader performance data (includes margin_balance + pseudonym)
      if (session.trader_uid) {
        try {
          const perf = await traderApi.getTraderPerformance(session.trader_uid);
          return {
            ...session,
            trader_name: shownTraderName(perf.trader_name || session.trader_name, session.trader_uid),
            margin_balance: perf.margin_balance || 0,
            // Don't override trader PnL with lifetime stats — keep session-scoped value
          };
        } catch {
          // If trader fetch fails, return session as-is
        }
      }
      return { ...session, margin_balance: 0 };
    },
    enabled: !!sessionId && !!sessions,
  });
}

/*
 * Every order of the session, not the newest 500 (Guillaume, 2026-10-07:
 * "these row limit should not exist anywhere"). Pages of 500, the endpoint's
 * page size, fetched to the end; `data` stays a flat array, as the page reads
 * it, and `total` is the server's count.
 */
export function useSessionOrders(sessionId: string, demoMode = false) {
  const q = useInfiniteQuery({
    queryKey: ['subscription-orders', sessionId],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => subscriptionApi.getOrdersPage(sessionId, { limit: 500, cursor: pageParam }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!sessionId,
  });
  useFetchAllPages(q);
  const flat = useMemo(() => (q.data?.pages ?? []).flatMap((p) => p.orders), [q.data]);
  return { ...q, data: flat, total: q.data?.pages?.[0]?.total ?? flat.length, hasMore: q.hasNextPage };
}

export function useSessionPositions(sessionId: string) {
  return useQuery({
    queryKey: ['subscription-positions', sessionId],
    queryFn: async () => {
      const positions = await subscriptionApi.getPositions(sessionId);
      return positions || [];
    },
    enabled: !!sessionId,
    refetchInterval: 5000, // refresh every 5s for live PnL
  });
}

/*
 * Every closed trade of the session, not the newest 5,000, which the server
 * also capped silently (2026-10-08). Pages of 5,000 fetched to the end.
 */
export function useSessionTrades(sessionId: string, _demoMode = false) {
  const q = useInfiniteQuery({
    queryKey: ['subscription-trades', sessionId, 'closed'],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => subscriptionApi.getSessionTradesPage(sessionId, { limit: 5000, cursor: pageParam }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!sessionId,
    staleTime: 10000,
    // Keep the Closed Trades list fresh in lockstep with the Performance Metrics.
    refetchInterval: 10000,
  });
  useFetchAllPages(q);
  const flat = useMemo(() => (q.data?.pages ?? []).flatMap((p) => p.trades), [q.data]);
  return { ...q, data: flat, total: q.data?.pages?.[0]?.total ?? flat.length, hasMore: q.hasNextPage };
}

export function useSessionOpenTrades(sessionId: string) {
  return useQuery({
    queryKey: ['subscription-trades', sessionId, 'open'],
    queryFn: async () => {
      if (!sessionId) return [];
      // Every open position: the server no longer cuts this list (2026-10-08).
      return subscriptionApi.getSessionTrades(sessionId, 'OPEN');
    },
    enabled: !!sessionId,
    refetchInterval: 10000,
  });
}

/*
 * The recorded wallet of the key a session trades on. Hourly on the server, so
 * a five-minute poll loses nothing; a demo session has no server row to ask.
 */
export function useSessionWalletHistory(sessionId: string, demoMode = false) {
  return useQuery({
    queryKey: ['subscription-wallet-history', sessionId],
    queryFn: () => subscriptionApi.getSessionWalletHistory(sessionId),
    enabled: !!sessionId && !demoMode,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

export function useSessionPerformance(sessionId: string) {
  return useQuery({
    queryKey: ['subscription-performance', sessionId],
    queryFn: async () => {
      if (!sessionId) return null;
      return subscriptionApi.getSessionPerformance(sessionId);
    },
    enabled: !!sessionId,
    staleTime: 10000,
    // Auto-refresh so the Performance Metrics stay in sync with the Closed Trades
    // list as new trades close, instead of showing stale values. 2026-07-05.
    refetchInterval: 10000,
  });
}
